import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useImportContracts, usePostAllContracts, usePostAllContractsExport } from '../hooks/api/contractsListApi';
import DataTableBodyCellGraph from '../../../components/DataTable/DataTableBody/DataTableBodyCell/DataTableBodyCellGraph';
import { withStyles } from '@material-ui/core/styles';
import { LinearProgress } from '@material-ui/core';
import Add from '@material-ui/icons/Add';
import PropTypes from 'prop-types';
import FullList from '../../../components/FullList';
import { useHistory } from 'react-router';
import FileUpload from '../../../components/FileUpload/FileUpload';
import FileExport from '../../../components/FileExport/FileExport';
import { FormattedMessage, useIntl } from 'react-intl';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import useAppContext from '../../../hooks/context/useAppContext';
import { mapEnumsToFilterList, remapObjectArrayToFilterList } from '../../../utils/objectArray';
import { useOrganizationEnum } from '../../../hooks/api/organizationEnumApi';
import { useUsersEnum } from '../../../hooks/api/usersEnumApi';
import { contractsColumns } from '../enums/columns';
import DataTableBodyCellDecimal from '../../../components/DataTable/DataTableBody/DataTableBodyCell/DataTableBodyCellDecimal';
import { handleUploadResult } from '../../../utils/fileUploadErrorsCheck';
import DoneAllIcon from '@material-ui/icons/DoneAll';
import { BuyMultipleContracts } from '../../Contracts/buy/BuyMultipleContracts';
import DialogModal from '../../../components/DialogModal/DialogModal';
import CustomSnackbar from '../../../components/CustomizedComponents/CustomSnackbar';
import { useGetProductsForMultContracts, usePostProductsForMultContracts } from '../../Contracts/hooks/api/contractsApi';
import { useForm } from 'react-hook-form';
import userRoles from '../../../constants/userRoles';

const contractEdit = 'contract';
const contractNew = 'contracts/new/basic-information';
const sessionFilterStorageName = 'contractsFilter';

const ContractList = ({ columns, showToolbarIcons }) => {
  const { getAllContracts, data, loading, error } = usePostAllContracts();
  const { getAllContractsExport, data: exportData, error: exportError, loading: exportLoading } = usePostAllContractsExport();
  const { uploadContracts, data: importData, error: importError, loading: importLoading } = useImportContracts();
  const { getProducts, data: productsData, error: getError, loading: getLoading } = useGetProductsForMultContracts();
  const { postProducts, error: postError, loading: postLoading } = usePostProductsForMultContracts();
  const { handleSubmit, register, errors: inputErrors } = useForm();
  const { getOrganizationEnum } = useOrganizationEnum();
  const { getUsersEnum } = useUsersEnum();
  const history = useHistory();
  const intl = useIntl();
  const success = intl.formatMessage({ id: 'app.action.saved' });
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [refresh, setRefresh] = useState(null);
  const [searchQuery, setSearchQuery] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [chosenContracts, setChosenContracts] = useState();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState(success);
  const [issues, setIssues] = useState(null);
  const { user, enums } = useAppContext();

  const storeFilter = JSON.parse(sessionStorage.getItem(sessionFilterStorageName));

  const initialValues = {
    filters: storeFilter?.columnsFilter || {
      organizationId: user?.customerIds,
      state: [0],
    },
    page: storeFilter?.page || 0,
    rowsPerPage: storeFilter?.size || user?.initialRowsPerPage,
    order: storeFilter?.order || 'asc',
    orderBy: storeFilter?.orderBy || 'organizationName',
    fulltext: storeFilter?.fulltext || '',
    filterOptions: storeFilter?.filterOptions || {
      state: [{ id: 0 }, { id: 1 }],
    },
  };

  const checkboxTools = [
    {
      onClick: (transactions) => {
        const filteredContracts = [];
        data.data.forEach(({ id, idExternal, commodity, contractPeriod }) => {
          if (transactions.includes(id)) {
            filteredContracts.push(
              !filteredContracts[0] ?
                { id: id, idExternal: idExternal, commodityId: commodity.id, contractPeriod: contractPeriod } :
                { id: id, idExternal: idExternal },
            );
          }
        });
        setChosenContracts(filteredContracts);
        setIsModalOpen(true);
      },
      icon: <DoneAllIcon />,
      label: <FormattedMessage id={'app.contracts.bulkPurchase'} />,
      tooltip: <FormattedMessage id={'app.dataTable.toolbarIconTooltip.done'} />,
    },
  ];

  const toolbarComponents = [
    <FileUpload upload={uploadContracts} tooltip={<FormattedMessage id={'app.dataTable.toolbarIconTooltip.import'} />} key={0} />,
    <FileExport
      getAllEntityExport={getAllContractsExport}
      tooltip={<FormattedMessage id={'app.dataTable.toolbarIconTooltip.export'} />}
      fileName={'EEM_smlouvy_export.xlsx'}
      data={exportData}
      searchQuery={searchQuery}
      exportError={exportError}
      setErrorMessage={setErrorMessage}
      key={1}
    />,
    <Tooltip title={<FormattedMessage id={'app.dataTable.toolbarIconTooltip.add'} />} arrow key={2}>
      <IconButton size={'small'} onClick={() => history.push(`/${contractNew}`)}>
        <Add fontSize={'large'} />
      </IconButton>
    </Tooltip>,
  ];

  const getPriceSourceValue = (isBeskAksAvailable, isSettlementAvailable) => {
    if (isBeskAksAvailable && isSettlementAvailable) {
      return 'Settlement + Bestask';
    }

    return isBeskAksAvailable ? 'Bestask' : 'Settlement';
  };

  useEffect(() => {
    handleUploadResult(importData, importError, setSuccessMessage, setErrorMessage, setRefresh);
  }, [importData, importError]);

  useEffect(() => {
    const currentError = getError || postError;
    if (currentError) {
      setMessage(currentError?.data?.message);
      setIssues(currentError?.data?.issues);
    } else {
      setMessage(success);
      setIssues(null);
    }
  }, [success, getError, postError]);

  const processData = (dataToProcess) => {
    return {
      data: dataToProcess.data.map((row) => {
        const StyledProgressBar = withStyles({
          colorPrimary: {
            backgroundColor: `#b8b3ad`,
          },
          barColorPrimary: {
            backgroundColor: `#f24f00`,
          },
        })(LinearProgress);

        const totalPercent = row.percentagePurchasedQuantity;

        const output = {
          id: row.id,
          state: row.state ? (
            <FormattedMessage id={'app.pages.customerListPage.state.active'} />
          ) : (
            <FormattedMessage id={'app.pages.customerListPage.state.inactive'} />
          ),
          idExternal: row.idExternal,
          frameContract: row.frameContract,
          commodity: row.commodity.name,
          period: row.contractPeriod,
          organizationName: row.customer.name,
          companyID: row.customer.organizationNumber,
          kam: row.customer.users
            .filter((user) => user.role === 'KAM')
            .map((user) => `${user.firstname} ${user.surname}`)
            .join(', '),
          currency: row.currency.id,
          invoicingCurrency: row.invoicingCurrency.id,
          percentagePurchasedQuantity: (
            <DataTableBodyCellGraph
              graph={<StyledProgressBar variant="determinate" value={totalPercent} />}
              text={
                <>
                  <DataTableBodyCellDecimal value={totalPercent} />%
                </>
              }
            />
          ),
          purchased: row.purchasedQuantity,
          quantity: <DataTableBodyCellDecimal value={row.quantity} />,
          remainingPurchaseQuantity: <DataTableBodyCellDecimal value={row.remainingPurchaseQuantity} />,
          contract: `${row.commodity?.name} ${row.contractPeriod}`,
          product: row.supplyProduct?.name,
          isMonthlyPurchaseAvailable: row?.isMonthlyPurchaseAvailable ? (
            <FormattedMessage id={'app.billingprices.product.intrayear'} />
          ) : (
            <FormattedMessage id={'app.billingprices.product.year'} />
          ),
          contractNumber: row.idExternal,
          supplyProduct: row.supplyProduct?.name,
          purchaseIndexType: row.purchaseIndexType?.name,
          purchaseMethod: row.purchaseMethod?.name,
          priceSource: getPriceSourceValue(row.isBeskAksAvailable, row.isSettlementAvailable),
          isSale: row.isSaleAvailable ? 'ANO' : 'NE',
          sapId: row?.customer?.idExternal,
          remainingPurchaseQuantityM1: null,
          remainingPurchaseQuantityM2: null,
          remainingPurchaseQuantityM3: null,
          remainingPurchaseQuantityM4: null,
          remainingPurchaseQuantityM5: null,
          remainingPurchaseQuantityM6: null,
          remainingPurchaseQuantityM7: null,
          remainingPurchaseQuantityM8: null,
          remainingPurchaseQuantityM9: null,
          remainingPurchaseQuantityM10: null,
          remainingPurchaseQuantityM11: null,
          remainingPurchaseQuantityM12: null,
        };

        row.monthVolumes.forEach((monthVolumes) => {
          output[`remainingPurchaseQuantityM${monthVolumes?.month}`] = <DataTableBodyCellDecimal value={monthVolumes?.remainingPurchaseQuantity} />;
        });

        return output;
      }),
      totalCount: dataToProcess.totalCount,
    };
  };

  const columnFilters = useMemo(
    () => ({
      [contractsColumns.kam.id]: async () => getUsersEnum('KAM'),
      [contractsColumns.organisation.id]: async () => getOrganizationEnum(),
      [contractsColumns.commodity.id]: () => mapEnumsToFilterList(enums?.['commodities']),
      [contractsColumns.currency.id]: () => mapEnumsToFilterList(enums?.['currencies']),
      [contractsColumns.invoicingCurrency.id]: () => mapEnumsToFilterList(enums?.['currencies']),
      [contractsColumns.deliveryProduct.id]: () => mapEnumsToFilterList(enums?.['supplyProducts']),
      [contractsColumns.pnMethod.id]: () => mapEnumsToFilterList(enums?.['purchaseIndexTypes']),
      [contractsColumns.purchaseMethod.id]: () => mapEnumsToFilterList(enums?.['purchaseMethods']),
      [contractsColumns.priceType.id]: () => mapEnumsToFilterList(enums?.['priceSources']),
      [contractsColumns.contractPeriod.id]: () => remapObjectArrayToFilterList(enums?.['years'], 'year', 'year'),
      [contractsColumns.state.id]: () => [
        {
          id: 1,
          label: intl.formatMessage({ id: 'app.pages.customerListPage.state.active' }),
        },
        {
          id: 0,
          label: intl.formatMessage({ id: 'app.pages.customerListPage.state.inactive' }),
        },
      ],
      [contractsColumns.productType.id]: () => [
        {
          id: true,
          label: intl.formatMessage({ id: 'app.billingprices.product.intrayear' }),
        },
        {
          id: false,
          label: intl.formatMessage({ id: 'app.billingprices.product.year' }),
        },
      ],
    }),
    [getUsersEnum, getOrganizationEnum, enums, intl],
  );

  const onFilterOpen = useCallback(
    async (column) => {
      return columnFilters?.[column] && columnFilters[column]();
    },
    [columnFilters],
  );

  return (
    <>
      <FullList
        getData={getAllContracts}
        loading={loading || importLoading || exportLoading}
        error={error}
        data={data}
        processData={processData}
        columns={columns}
        initialValues={initialValues}
        showToolbarIcons={showToolbarIcons}
        toolbarComponents={toolbarComponents}
        onLineClick={(line) => {
          history.push(`/${contractEdit}/${line.id}`);
        }}
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
        successMessage={successMessage}
        setSuccessMessage={setSuccessMessage}
        refresh={refresh}
        setRefresh={setRefresh}
        onFilterOpen={onFilterOpen}
        setSearchQuery={setSearchQuery}
        sessionFilterStorageName={sessionFilterStorageName}
        checkboxColumn={user.role === userRoles.ADMIN ? true : false}
        checkboxTools={user.role === userRoles.ADMIN ? checkboxTools : []}
      />
      <DialogModal isModalOpen={isModalOpen} dialogTitle={'app.contracts.label'} cancelAction={() => setIsModalOpen(false)}>
        <BuyMultipleContracts
          chosenContracts={chosenContracts}
          confirmBtnTitle={'app.buttons.deal'}
          cancelBtnTitle={'app.cancel'}
          setIsModalOpen={setIsModalOpen}
          setOpen={setOpen}
          handleSubmit={handleSubmit}
          register={register}
          inputErrors={inputErrors}
          getProducts={getProducts}
          postProducts={postProducts}
          getLoading={getLoading}
          postLoading={postLoading}
          productsData={productsData}
        />
      </DialogModal>

      {!postLoading && message && <CustomSnackbar status={!postError ? 'success' : 'error'} message={message} setOpen={setOpen} open={open} />}
      {postError && issues && <CustomSnackbar key={'issues'} status={'error'} message={issues.join(', ')} setOpen={setOpen} open={open} />}
    </>
  );
};

ContractList.propTypes = {
  columns: PropTypes.array.isRequired,
  showToolbarIcons: PropTypes.bool.isRequired,
};

export default ContractList;
